import { Button, Card, Divider, Input, List, Modal, Select, Space, Typography } from 'antd';
import Title from 'antd/es/typography/Title';
import { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import TipTap from '../../Sections/TipTap';
import { ExclamationCircleFilled } from '@ant-design/icons';

interface OverviewProps {
  data: any;
  setData: (path: string, value: any) => void;
  readonly?: boolean;
}

const Overview: React.FC<OverviewProps> = ({ data, setData, readonly = false }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [scopeColumnToRemove, setScopeColumnToRemove] = useState<string | null>(null);

  const handleRemoveScopeColumn = (scopeColumnId: string) => {
    if (readonly) return;
    setScopeColumnToRemove(scopeColumnId);
    setIsModalOpen(true);
  };

  const handleConfirmRemoveScopeColumn = () => {
    if (readonly) return;
    if (scopeColumnToRemove) {
      const updatedScopeColumns = data.overview.scopeColumns.filter(
        (scopeColumn: any) => scopeColumn.id !== scopeColumnToRemove
      );
      setData('methodologies.overview.scopeColumns', updatedScopeColumns);
    }
    setIsModalOpen(false);
  };

  const handleCancelRemoveScopeColumn = () => {
    setIsModalOpen(false);
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Title level={3}>Overview</Title>

      <Card>
        {data.overview.scopeColumns.map((scope, index) => (
          <Space direction="vertical" style={{ width: '100%', marginBottom: '20px' }}>
            <Space
              direction="horizontal"
              style={{ justifyContent: 'space-between', width: '100%' }}>
              <Typography.Title level={5}>Scope Column no. {index + 1}</Typography.Title>
              <Button
                type="primary"
                danger
                disabled={readonly}
                style={{ marginTop: '10px' }}
                onClick={() => handleRemoveScopeColumn(scope.id)}>
                Remove
              </Button>
            </Space>
            <Input
              placeholder="Name"
              disabled={readonly}
              value={scope.name}
              onChange={(e) => {
                if (readonly) return;
                const updatedScopeColumns = data.overview.scopeColumns.map((scopeColumn, i) => {
                  if (scopeColumn.id === scope.id) {
                    return {
                      ...scopeColumn,
                      name: e.target.value,
                      key: e.target.value.toLowerCase().trim().replace(/\s/g, '')
                    };
                  }
                  return scopeColumn;
                });
                setData('methodologies.overview.scopeColumns', updatedScopeColumns);
              }}
            />
            <Space align="center">
              <Title level={5} style={{ marginTop: '5px' }}>
                Column size:
              </Title>
              <Select
                placeholder="Select width size"
                disabled={readonly}
                value={scope.widthSize}
                style={{ width: '200px' }}
                onChange={(value) => {
                  if (readonly) return;
                  const updatedScopeColumns = data.overview.scopeColumns.map((scopeColumn, i) => {
                    if (scopeColumn.id === scope.id) {
                      return {
                        ...scopeColumn,
                        widthSize: value
                      };
                    }
                    return scopeColumn;
                  });
                  setData('methodologies.overview.scopeColumns', updatedScopeColumns);
                }}>
                <Select.Option value="xs">Extra Small</Select.Option>
                <Select.Option value="sm">Small</Select.Option>
                <Select.Option value="md">Medium</Select.Option>
                <Select.Option value="lg">Large</Select.Option>
                <Select.Option value="xl">Extra Large</Select.Option>
              </Select>
            </Space>
          </Space>
        ))}
        <Button
          type="primary"
          disabled={readonly}
          style={{ marginTop: '10px' }}
          onClick={() => {
            if (readonly) return;
            setData('methodologies.overview.scopeColumns', [
              ...data.overview.scopeColumns,
              { id: uuidv4(), name: '', key: '', widthSize: 'md' }
            ]);
          }}>
          Add Scope Column
        </Button>
      </Card>
      <Title level={5}>Citation list (enter as a numbered list)</Title>
      <TipTap
        value={data.overview.citations}
        readonly={readonly}
        onChange={(value) => {
          if (readonly) return;
          setData('methodologies.overview.citations', value);
        }}
      />
      {data.overview.data.map((methodology, index) => (
        <Space direction="vertical" style={{ width: '100%' }}>
          <Title level={3}>Row #{index + 1}</Title>
          <Input placeholder="Standard" value={methodology.standard} readOnly />
          <Input placeholder="Methodology" value={methodology.methodology} readOnly />
          <Input placeholder="Version" value={methodology.version} readOnly />
          {[
            { key: 'additionality', title: 'Additionality' },
            { key: 'overCrediting', title: 'Over Crediting' },
            { key: 'permanence', title: 'Permanence' },
            { key: 'overLappingClaims', title: 'Overlapping Claims' }
          ].map((item) => (
            <Space direction="vertical" style={{ width: '100%' }}>
              <Title level={5}>{item.title}</Title>
              {methodology[item.key].map(({ impact, description, id }, index) => (
                <Space direction="horizontal" key={index}>
                  <Select
                    value={impact}
                    disabled={readonly}
                    onChange={(value) => {
                      if (readonly) return;
                      const updatedMethodologies = data.overview.data.map((m) => {
                        if (m.id === methodology.id) {
                          return {
                            ...m,
                            [item.key]: m[item.key].map((imp) =>
                              imp.id === id ? { ...imp, impact: value } : imp
                            )
                          };
                        }
                        return m;
                      });

                      setData('methodologies.overview.data', updatedMethodologies);
                    }}
                    style={{ width: 200 }}
                    placeholder="Select impact level">
                    <Select.Option value="none">None</Select.Option>
                    <Select.Option value="low">Low</Select.Option>
                    <Select.Option value="low_to_medium">Low to Medium</Select.Option>
                    <Select.Option value="medium">Medium</Select.Option>
                    <Select.Option value="medium_to_high">Medium to High</Select.Option>
                    <Select.Option value="high">High</Select.Option>
                  </Select>
                  <Input
                    value={description}
                    disabled={readonly}
                    onChange={(e) => {
                      if (readonly) return;
                      const updatedMethodologies = data.overview.data.map((m) => {
                        if (m.id === methodology.id) {
                          return {
                            ...m,
                            [item.key]: m[item.key].map((impact, i) =>
                              impact.id === id ? { ...impact, description: e.target.value } : impact
                            )
                          };
                        }
                        return m;
                      });
                      setData('methodologies.overview.data', updatedMethodologies);
                    }}
                    placeholder="Qualifying text (optional) E.g. ‘(Flare only)’"
                    style={{ width: 300 }}
                  />
                  {/* remove impact button */}
                  <Button
                    type="primary"
                    danger
                    onClick={() => {
                      if (readonly) return;
                      const updatedMethodologies = data.overview.data.map((m) => {
                        if (m.id === methodology.id) {
                          return {
                            ...m,
                            [item.key]: m[item.key].filter((impact) => impact.id !== id)
                          };
                        }
                        return m;
                      });
                      setData('methodologies.overview.data', updatedMethodologies);
                    }}
                    disabled={readonly}>
                    Remove
                  </Button>
                </Space>
              ))}
              <Space style={{ width: '100%' }}>
                <Button
                  type="primary"
                  disabled={readonly}
                  onClick={() => {
                    if (readonly) return;
                    const updatedMethodologies = data.overview.data.map((m) => {
                      if (m.id === methodology.id) {
                        return {
                          ...m,
                          [item.key]: [
                            ...m[item.key],
                            { id: uuidv4(), impact: '', description: '' }
                          ]
                        };
                      }
                      return m;
                    });
                    setData('methodologies.overview.data', updatedMethodologies);
                  }}>
                  Add Impact
                </Button>
              </Space>
            </Space>
          ))}
          {data.overview.scopeColumns.length > 0 && (
            <Typography.Title level={5} style={{ marginTop: '20px', color: 'red' }}>
              Tip: keep all scope / eligibility responses as brief as possible to avoid overloading
              the table
            </Typography.Title>
          )}
          {data.overview.scopeColumns.map((scopeColumn, index) => (
            <Space direction="vertical" style={{ width: '100%', marginBottom: '20px' }}>
              <Typography.Title level={5}>{scopeColumn.name}</Typography.Title>
              <TipTap
                value={methodology[scopeColumn.key]}
                readonly={readonly}
                onChange={(value) => {
                  if (readonly) return;
                  const updatedMethodologies = data.overview.data.map((m) => {
                    if (m.id === methodology.id) {
                      return {
                        ...m,
                        [scopeColumn.key]: value
                      };
                    }
                    return m;
                  });
                  setData('methodologies.overview.data', updatedMethodologies);
                }}
              />
            </Space>
          ))}
          <Space direction="vertical" style={{ width: '100%', marginTop: '20px' }}>
            <Typography.Title level={5}>Corsia</Typography.Title>
            <Space
              direction="vertical"
              style={{
                background: '#eee',
                width: '100%',
                borderRadius: '5px',
                padding: '10px',
                marginBottom: '10px'
              }}>
              <List
                size="small"
                bordered={false}
                split={false}
                header={
                  <Typography.Text strong style={{ fontSize: '16px' }}>
                    Note <ExclamationCircleFilled style={{ color: 'orange' }} />
                  </Typography.Text>
                }
                style={{ listStyleType: 'circle' }}>
                <List.Item style={{ paddingLeft: 0, padding: 0 }}>
                  <Typography.Text strong style={{ fontSize: '13px' }}>
                    To link to the Corsia eligibility criteria details section, select the text you
                    want to link, click the link icon, and click the okay button without adding any
                    text into the link field
                  </Typography.Text>
                </List.Item>
              </List>
            </Space>
            <TipTap
              value={methodology.corsia}
              readonly={readonly}
              onChange={(value) => {
                if (readonly) return;
                const updatedMethodologies = data.overview.data.map((m) => {
                  if (m.id === methodology.id) {
                    return {
                      ...m,
                      corsia: value
                    };
                  }
                  return m;
                });
                setData('methodologies.overview.data', updatedMethodologies);
              }}
            />
          </Space>
          <Space direction="vertical" style={{ width: '100%', marginTop: '20px' }}>
            <Typography.Title level={5}>CCP Status</Typography.Title>
            <TipTap
              value={methodology.icvcm}
              readonly={readonly}
              onChange={(value) => {
                if (readonly) return;
                const updatedMethodologies = data.overview.data.map((m) => {
                  if (m.id === methodology.id) {
                    return {
                      ...m,
                      icvcm: value
                    };
                  }
                  return m;
                });
                setData('methodologies.overview.data', updatedMethodologies);
              }}
            />
          </Space>
          <Divider />
        </Space>
      ))}
      <Modal
        title="Confirm removal of scope column"
        open={isModalOpen}
        onOk={handleConfirmRemoveScopeColumn}
        onCancel={handleCancelRemoveScopeColumn}>
        All the tabs will be affected, are you sure you want to remove this scope column?
      </Modal>
    </Space>
  );
};

export default Overview;
